import React from 'react';
import { styled } from '@compiled/react';
import InlineMessage from '@atlaskit/inline-message';
import { token } from '@atlaskit/tokens';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import HtmlFormattedMessage from '@atlassian/jira-servicedesk-common/src/i18n/format-html-message';
import { ATLASSIAN_SUPPORT_URL } from '@atlassian/jira-servicedesk-common/src/ui/components/limit-message';
import type { Intl, FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { ABOUT_TO_REACH_MAX, MAX_REACHED } from '../../../../../../../common/ui/constants.tsx';
import messages from './messages';
import { getLimitState } from './utils';

type LimitInlineViewProps = Intl & {
	goalsLimit: number;
	remaining: number;
};

const getLimitInlinePopupView = (formatMessage: FormatMessage, goalsLimit: number) => (
	<>
		<p>{formatMessage(messages.totalLimit, { goalsLimit })}</p>
		<HtmlFormattedMessage
			{...messages.tipForCreatingNewGoals}
			values={{
				tagStart: `<a href="${ATLASSIAN_SUPPORT_URL}" target="_blank"/>`,
				tagEnd: '</a>',
			}}
		/>
	</>
);

const LimitInlineView = ({
	intl: { formatMessage },
	goalsLimit,
	remaining,
}: LimitInlineViewProps) => {
	const limitState = getLimitState(goalsLimit, remaining);
	if (limitState === ABOUT_TO_REACH_MAX) {
		return (
			<Spaced>
				<InlineMessage
					appearance="info"
					title={formatMessage(messages.aboutToReachMaxLimit, { remaining })}
				>
					{getLimitInlinePopupView(formatMessage, goalsLimit)}
				</InlineMessage>
			</Spaced>
		);
	}

	if (limitState === MAX_REACHED) {
		return (
			<Spaced>
				<InlineMessage
					appearance="warning"
					title={formatMessage(messages.maxReached, { goalsLimit })}
				>
					{getLimitInlinePopupView(formatMessage, goalsLimit)}
				</InlineMessage>
			</Spaced>
		);
	}

	return null;
};

export const LimitInline = injectIntl(LimitInlineView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spaced = styled.div({
	margin: token('space.100', '8px'),
});
